import { useMemo } from "react";

import { filterFactory } from "@similarweb/pro-logic-components";
import type { SelectOption } from "@similarweb/pro-logic-components/dist/filter-factory/src/compound";
import { Box } from "@similarweb/ui-components/foundations";

import categoryService, { ALL } from "app/common/services/categoryService";
import type { ICategory } from "app/common/services/categoryService.types";
import { subCategoryFilter } from "app/filters/ngFilters";
import { translate } from "app/filters/i18nFilter";

import { useDisplayAdvertiserPublisherDataAndParams } from "../hooks/useDisplayAdvertiserPublisherDataAndParams";
import type { AdIntelligenceController } from "../shared/types/types";

interface IAdvertisersPublishersFilterState {
    industry?: string;
    network?: string;
}

const Filters = filterFactory.createTypedFilters<IAdvertisersPublishersFilterState>();

interface IDisplayAdvertisersPublishersFiltersProps {
    controller: AdIntelligenceController;
    resource: string;
    onFilterChange: (value: IAdvertisersPublishersFilterState) => void;
}

export const DisplayAdvertisersPublishersFilters = ({
    controller,
    resource,
    onFilterChange,
}: IDisplayAdvertisersPublishersFiltersProps) => {
    const params = useDisplayAdvertiserPublisherDataAndParams();

    const industriesForFilter: SelectOption[] = useMemo(() => {
        return categoryService
            .getCategories(false, { excludeAdult: true })
            .filter((item) => resource !== "publisher" || item.id !== ALL)
            .flatMap(
                (item) =>
                    [
                        {
                            id: item.id,
                            value: item.id,
                            label: item.text,
                            icon: <i className={item.icon} />,
                        } as SelectOption,
                        ...(Array.isArray(item?.children)
                            ? item.children.map((son: ICategory) => ({
                                  id: son.id,
                                  value: son.id,
                                  label: son.text,
                                  icon: <Box width="14px" />,
                              }))
                            : []),
                    ] as SelectOption[],
            )
            .map((item) => ({ ...item, children: undefined }));
    }, [resource]);

    const selectedIndustryLabel = params.category
        ? subCategoryFilter()(params.category)
        : translate(`ad.intelligence.${controller}.${resource}s.table.industry.label`);

    return (
        <Filters.Provider
            state={{
                industry: params.category,
                network: params.network,
            }}
            onChange={onFilterChange}
        >
            <Filters.Select
                name="industry"
                label={selectedIndustryLabel}
                options={industriesForFilter}
                isDisabled={!industriesForFilter?.length}
                withSearch
            />
        </Filters.Provider>
    );
};
